import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SubscriptionGate } from '@/components/paywall/SubscriptionGate';
import { formatMinutesSeconds } from '@/lib/format';
import { learnerApiJson } from '@/lib/learner-api';
import { crosswordPlayGateCopy } from '@/lib/paywall';
import { enqueueGameRun } from '@/lib/progress-queue';
import { useLearnerAuth } from '@/providers/LearnerAuth';
import { usePremiumAccess } from '@/hooks/usePremiumAccess';
import { track } from '@/lib/analytics';

type Cell = { row: number; col: number; solution: string; number?: number | null };
type Clue = { id: string; number: number; clue: string; answer: string };
type Puzzle = {
  id: number;
  title: string;
  subtitle: string | null;
  grid: { rows: number; cols: number; cells: Cell[] };
  clues: { across: Clue[]; down: Clue[] };
};
type Direction = 'across' | 'down';

export const Route = createFileRoute('/app/crosswords/$date')({ component: CrosswordPage });

function keyOf(row: number, col: number) {
  return `${row}:${col}`;
}

export function CrosswordPage() {
  const { date } = Route.useParams();
  const auth = useLearnerAuth();
  const premium = usePremiumAccess();
  const navigate = useNavigate();
  const started = useRef(performance.now());
  const [now, setNow] = useState(performance.now());
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [completed, setCompleted] = useState(false);
  const [direction, setDirection] = useState<Direction>('across');
  const [active, setActive] = useState<{ row: number; col: number } | null>(null);
  const puzzle = useQuery({
    queryKey: ['crossword', date],
    queryFn: () => learnerApiJson<Puzzle>(auth?.getToken ?? null, `/v1/crosswords/${date}`),
    enabled: premium.isSubscribed,
  });
  const cellsByPosition = useMemo(
    () => new Map(puzzle.data?.grid.cells.map((cell) => [keyOf(cell.row, cell.col), cell]) ?? []),
    [puzzle.data],
  );

  useEffect(() => {
    if (completed) return;
    const timer = window.setInterval(() => setNow(performance.now()), 250);
    return () => window.clearInterval(timer);
  }, [completed]);

  if (premium.isReady && !premium.isSubscribed) {
    const gate = crosswordPlayGateCopy();
    return (
      <SubscriptionGate
        source={gate.source}
        content="crosswords"
        message={gate.message}
        heroEmoji={gate.heroEmoji}
        onDismiss={() => void navigate({ to: '/app/crosswords' })}
      />
    );
  }
  if (!puzzle.data) return <main className="grid min-h-[75vh] place-items-center">Loading puzzle…</main>;

  const wordCells = (row: number, col: number, dir: Direction) => {
    const delta = dir === 'across' ? [0, 1] : [1, 0];
    let r = row;
    let c = col;
    while (cellsByPosition.get(keyOf(r - delta[0], c - delta[1]))) {
      r -= delta[0];
      c -= delta[1];
    }
    const cells: Cell[] = [];
    while (true) {
      const cell = cellsByPosition.get(keyOf(r, c));
      if (!cell) break;
      cells.push(cell);
      r += delta[0];
      c += delta[1];
    }
    return cells;
  };

  const current = active ?? puzzle.data.grid.cells[0];
  const highlighted = current ? new Set(wordCells(current.row, current.col, direction).map((cell) => keyOf(cell.row, cell.col))) : new Set<string>();
  const startCell = current ? wordCells(current.row, current.col, direction)[0] : undefined;
  const activeClue =
    startCell?.number != null
      ? puzzle.data.clues[direction].find((clue) => clue.number === startCell.number) ?? null
      : null;

  const update = (cell: Cell, value: string) => {
    const next = { ...answers, [keyOf(cell.row, cell.col)]: value.slice(-1).toLocaleUpperCase() };
    setAnswers(next);
    const word = wordCells(cell.row, cell.col, direction);
    const index = word.findIndex((item) => item.row === cell.row && item.col === cell.col);
    if (value && index >= 0 && index < word.length - 1) {
      setActive({ row: word[index + 1].row, col: word[index + 1].col });
    }
    const solved = puzzle.data.grid.cells.every(
      (candidate) => next[keyOf(candidate.row, candidate.col)]?.toLocaleLowerCase() === candidate.solution.toLocaleLowerCase(),
    );
    if (solved && !completed) {
      setCompleted(true);
      const duration = (performance.now() - started.current) / 1000;
      void enqueueGameRun({
        game_slug: 'crossword',
        game_display_name: 'Crossword',
        crossword_puzzle_id: puzzle.data.id,
        score: puzzle.data.grid.cells.length,
        duration_seconds: duration,
      });
      track('Crossword Completed', { puzzle_date: date, duration_seconds: duration });
    }
  };

  const focusClue = (clue: Clue, dir: Direction) => {
    const start = puzzle.data.grid.cells.find((cell) => cell.number === clue.number);
    if (!start) return;
    setDirection(dir);
    setActive({ row: start.row, col: start.col });
  };

  const timerText = formatMinutesSeconds((now - started.current) / 1000);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-8">
      <div className="flex items-center justify-between gap-3">
        <Link to="/app/crosswords" className="font-bold text-[#2d7a13]">
          ← Archive
        </Link>
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-white px-4 py-2 font-bold shadow-sm">{completed ? 'Done' : timerText}</span>
          <Link to="/app/crosswords/leaderboard" className="font-bold text-[#2d7a13]">
            Leaderboard
          </Link>
          {completed ? <span className="rounded-full bg-[#e5f3e7] px-4 py-2 font-bold text-[#276e17]">Completed!</span> : null}
        </div>
      </div>
      <h1 className="mt-6 font-nunito text-[22px] font-extrabold leading-[26px] text-[#1E1E1E]">{puzzle.data.title}</h1>
      {puzzle.data.subtitle ? <p className="mt-2 text-slate-600">{puzzle.data.subtitle}</p> : null}
      {activeClue ? (
        <button
          type="button"
          onClick={() => setDirection((value) => (value === 'across' ? 'down' : 'across'))}
          className="mt-5 w-full rounded-2xl bg-[#2d7a13] px-4 py-3 text-left text-white"
        >
          <span className="text-xs font-bold uppercase tracking-wider">{direction}</span>
          <p className="mt-1 font-bold">
            {activeClue.number}. {activeClue.clue.replace(/^Hmong word for\s+/i, '')}
          </p>
        </button>
      ) : null}
      <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div
          className="mx-auto grid aspect-square w-full max-w-[620px] gap-px bg-slate-800 p-1"
          style={{ gridTemplateColumns: `repeat(${puzzle.data.grid.cols}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: puzzle.data.grid.rows * puzzle.data.grid.cols }, (_, index) => {
            const row = Math.floor(index / puzzle.data.grid.cols);
            const col = index % puzzle.data.grid.cols;
            const cell = cellsByPosition.get(keyOf(row, col));
            if (!cell) return <span key={index} className="bg-slate-900" />;
            const focused = active?.row === row && active?.col === col;
            const inWord = highlighted.has(keyOf(row, col));
            return (
              <label
                key={index}
                className={`relative aspect-square ${inWord ? 'bg-[#fff0a8]' : 'bg-white'} ${focused ? 'ring-2 ring-[#399918]' : ''}`}
              >
                <span className="absolute left-0.5 top-0 text-[9px] font-bold">{cell.number}</span>
                <input
                  aria-label={`Crossword row ${row + 1}, column ${col + 1}`}
                  value={answers[keyOf(row, col)] || ''}
                  onFocus={() => setActive({ row, col })}
                  onChange={(event) => update(cell, event.target.value)}
                  maxLength={1}
                  className="h-full w-full bg-transparent text-center text-lg font-black uppercase outline-none sm:text-2xl"
                />
              </label>
            );
          })}
        </div>
        <aside className="space-y-6">
          {(['across', 'down'] as const).map((dir) => (
            <section key={dir} className="rounded-3xl border border-[#cfe8c6] bg-white p-5">
              <h2 className="text-lg font-black capitalize">{dir}</h2>
              <ol className="mt-3 space-y-2">
                {puzzle.data.clues[dir].map((clue) => (
                  <li key={clue.id}>
                    <button
                      type="button"
                      onClick={() => focusClue(clue, dir)}
                      className={`w-full rounded-lg px-2 py-1 text-left text-sm leading-6 ${
                        activeClue?.id === clue.id && direction === dir ? 'bg-[#ECFFE6] font-bold' : ''
                      }`}
                    >
                      <strong>{clue.number}.</strong> {clue.clue.replace(/^Hmong word for\s+/i, '')}
                    </button>
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </aside>
      </div>
    </div>
  );
}
